import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Empty, Input } from "antd";
import AppShell from "../../../components/layout/AppShell";
import StatCard from "../../../components/ui/StatCard";
import { listSenders } from "../../../services/api";
import DateFilter from "../../../components/ui/DateFilter";
import { getDateFilterRange } from "../../../utils";
import { DEFAULT_PAGE_SIZE_OPTIONS, DATE_FILTER_OPTIONS } from "../../../utils/constants";

// Kill-switch for this page's <DateFilter />.
const DISABLE_DATE_FILTER = true;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const kybLabel = (s) =>
  s === "APPROVED" ? "Approved" : s === "REJECTED" ? "Rejected" : "Pending";

const kybBadgeClass = (s) =>
  s === "APPROVED"
    ? "bg-green-50 text-green-700 border border-green-200"
    : s === "REJECTED"
    ? "bg-red-50 text-red-700 border border-red-200"
    : "bg-orange-50 text-orange-700 border border-orange-200";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Senders = () => {
  const navigate = useNavigate();

  const [senders, setSenders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [dateRange, setDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setLoading(true);
    listSenders({
      page,
      limit: pageSize,
      ...(debouncedSearch ? { searchQuery: debouncedSearch } : {}),
      ...(!DISABLE_DATE_FILTER && dateRange.startDate ? { from: dateRange.startDate } : {}),
      ...(!DISABLE_DATE_FILTER && dateRange.endDate ? { to: dateRange.endDate } : {}),
    })
      .then((res) => {
        const data = res.data || [];
        setSenders(data);
        setTotal(res.meta?.total || data.length);
      })
      .catch(() => setSenders([]))
      .finally(() => setLoading(false));
  }, [page, pageSize, debouncedSearch, dateRange]);

  const changePageSize = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const changeDateFilter = (_filter, range) => {
    setDateRange((prev) =>
      prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
    );
    setPage(1);
  };

  const approved = senders.filter((s) => s.status === "APPROVED").length;
  const pending = senders.filter((s) => s.status !== "APPROVED").length;

  const columns = [
    {
      title: "SN",
      key: "sn",
      width: 60,
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Name",
      key: "name",
      render: (_, s) => <span className="font-medium text-gray-900">{s.name || "—"}</span>,
    },
    {
      title: "Email",
      key: "email",
      render: (_, s) => s.email || "—",
    },
    {
      title: "Phone",
      key: "phone",
      render: (_, s) => s.phone || "—",
    },
    {
      title: "Country",
      key: "country",
      render: (_, s) => s.country || "—",
    },
    {
      title: "Joined",
      key: "joined",
      render: (_, s) => <span className="whitespace-nowrap">{formatDate(s.createdAt)}</span>,
    },
    {
      title: "KYB Status",
      key: "kyb_status",
      render: (_, s) => (
        <span
          className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${kybBadgeClass(s.status)}`}
        >
          {kybLabel(s.status)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, s) => (
        <button
          className="text-sm text-brand hover:underline whitespace-nowrap"
          onClick={() => navigate(`/receiver/senders/${s.id}`, { state: { sender: s } })}
        >
          View →
        </button>
      ),
    },
  ];

  return (
    <AppShell>
      {/* Warning banner when no senders */}
      {!loading && senders.length === 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl mb-5">
          <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm text-orange-700">
            No senders added yet. Please add a sender to start creating invoices.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Senders</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            List of senders mapped to this receiver.
          </p>
        </div>
        <button
          onClick={() => navigate("/receiver/senders/create")}
          className="bg-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brandHover transition-colors"
        >
          + Onboard Sender
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Total Senders"
          value={loading ? "—" : senders.length.toString()}
        />
        <StatCard
          label="KYB Approved"
          value={loading ? "—" : approved.toString()}
        />
        <StatCard
          label="Pending KYB"
          value={loading ? "—" : pending.toString()}
          valueColor={pending > 0 ? "text-orange-500" : undefined}
        />
      </div>

      {/* Senders table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-800 m-0">Your Senders</h2>
            {!loading && total > 0 && (
              <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full border border-gray-200">
                {total.toLocaleString()} total
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input.Search
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className="w-60"
            />
            {!DISABLE_DATE_FILTER && (
              <DateFilter onChange={changeDateFilter} />
            )}
          </div>
        </div>

        <Table
          className="admin-table table-blue-zebra"
          columns={columns}
          dataSource={senders}
          rowKey={(s) => s.id}
          loading={loading}
          locale={{
            emptyText: loading ? (
              <div className="py-12" />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Onboard your first sender to start creating invoices."
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: { showSearch: false },
            showQuickJumper: false,
            pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS.map(String),
            responsive: true,
            onShowSizeChange: (_current, newSize) => changePageSize(newSize),
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              if (newPageSize && newPageSize !== pageSize) changePageSize(newPageSize);
            },
            showTotal: (totalCount, range) => (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Showing{" "}
                <span className="font-medium text-gray-800">
                  {range[0]}-{range[1]}
                </span>{" "}
                of <span className="font-medium text-gray-800">{totalCount}</span> senders
              </span>
            ),
          }}
        />
      </div>
    </AppShell>
  );
};

export default Senders;
